import React, { useState, useEffect } from 'react'
import IntegrationEventCard from './IntegrationEventCard'
import { PERIOD_OPTIONS } from '../../utils/statsPeriod'
import { CHART_TYPES, useChartType } from '../../utils/useChartType'

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1)

const PERIOD_EMPTY_LABEL = {
  today: 'hoje',
  yesterday: 'ontem',
  '7d': 'nos últimos 7 dias',
  '15d': 'nos últimos 15 dias',
  '30d': 'nos últimos 30 dias'
}

const PeriodToggle = ({ period, onPeriodChange }) => (
  <div className="inline-flex items-center rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-0.5">
    {PERIOD_OPTIONS.map((opt) => (
      <React.Fragment key={opt.value}>
        {opt.value === '7d' && <span className="w-px h-4 mx-0.5 bg-gray-200 dark:bg-gray-700" />}
        <button
          type="button"
          onClick={() => onPeriodChange(opt.value)}
          className={`px-2.5 py-1 rounded-md text-xs font-medium transition ${
            period === opt.value
              ? 'bg-white dark:bg-gray-700 text-blue-700 dark:text-blue-300 shadow-sm'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
          }`}
        >
          {opt.label}
        </button>
      </React.Fragment>
    ))}
  </div>
)

const CHART_TYPE_OPTIONS = [
  { value: CHART_TYPES.PIE, icon: 'pie_chart', label: 'Gráfico de pizza' },
  { value: CHART_TYPES.LINE, icon: 'show_chart', label: 'Gráfico de linhas' },
  { value: CHART_TYPES.BAR, icon: 'bar_chart', label: 'Gráfico de barras' }
]

const ChartTypeToggle = ({ chartType, onChartTypeChange }) => (
  <div className="inline-flex items-center rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-0.5">
    {CHART_TYPE_OPTIONS.map((opt) => (
      <button
        key={opt.value}
        type="button"
        onClick={() => onChartTypeChange(opt.value)}
        title={opt.label}
        className={`w-7 h-7 flex items-center justify-center rounded-md transition ${
          chartType === opt.value
            ? 'bg-white dark:bg-gray-700 text-blue-700 dark:text-blue-300 shadow-sm'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
        }`}
      >
        <span className="material-icons" style={{ fontSize: '15px' }}>{opt.icon}</span>
      </button>
    ))}
  </div>
)

const formatTodayLabel = (date) => {
  const weekday = capitalize(date.toLocaleDateString('pt-BR', { weekday: 'long' }))
  const dateStr = date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })
  return `Hoje é ${weekday}, ${dateStr}`
}

const CardSkeleton = () => (
  <div className="bg-white/80 dark:bg-gray-800/70 backdrop-blur-sm rounded-xl border border-white/60 dark:border-white/10 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_14px_28px_-18px_rgba(0,0,0,0.22)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.3),0_14px_28px_-18px_rgba(0,0,0,0.7)] p-4 animate-pulse">
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-start gap-2.5 min-w-0 w-full">
        <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
        <div className="flex-1">
          <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
          <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
        </div>
      </div>
    </div>
    <div className="h-[110px] mt-3 bg-gray-100 dark:bg-gray-700/50 rounded-lg" />
  </div>
)

const IntegrationsOverview = ({ companyId, stats, loading, refreshing, onRefresh, username, integrations, onOpenTracesPage, onOpenErrorsPage, period, onPeriodChange }) => {
  const [now, setNow] = useState(new Date())
  const [cardTotals, setCardTotals] = useState({})
  const { chartType, setChartType } = useChartType()

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60000)
    return () => clearInterval(interval)
  }, [])

  // Reset only when the company actually changes - NOT on every `stats`
  // refresh. Each IntegrationEventCard below repopulates its own entry via
  // onTotalsLoaded whenever `stats` updates, and effects run children-first,
  // so resetting on `stats` here would always fire after and wipe out the
  // totals the cards just reported (this was why Sucessos/Erros were stuck
  // at zero).
  useEffect(() => {
    setCardTotals({})
  }, [companyId])

  const handleTotalsLoaded = (integrationId, totals) => {
    setCardTotals((prev) => ({ ...prev, [integrationId]: totals }))
  }

  const safeStats = stats || []
  const showSkeleton = loading || refreshing
  const isEmpty = !showSkeleton && safeStats.length === 0

  const totalEvents = safeStats.reduce((sum, s) => sum + (s.event_count || 0), 0)

  // Only tally entries for integrations still present in `stats` - guards
  // against a stale leftover entry from an integration that disappeared
  // (e.g. no events in the period anymore) between refreshes.
  const currentIntegrationIds = new Set(safeStats.map((s) => s.integration_id))
  const currentTotals = Object.entries(cardTotals).filter(([id]) => currentIntegrationIds.has(id))
  const totalSuccess = currentTotals.reduce((sum, [, t]) => sum + (t.success || 0), 0)
  const totalErrors = currentTotals.reduce((sum, [, t]) => sum + (t.error || 0), 0)
  const totalProcessing = currentTotals.reduce((sum, [, t]) => sum + (t.processing || 0), 0)
  const totalFinished = totalSuccess + totalErrors
  const successRate = totalFinished > 0 ? (totalSuccess / totalFinished) * 100 : 0

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4 pb-3 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-3 flex-wrap">
          {username && (
            <div className="flex items-center gap-3 pr-3 border-r border-gray-200 dark:border-gray-700">
              <div className="w-9 h-9 rounded-lg bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center flex-shrink-0">
                <span className="material-icons text-amber-500 dark:text-amber-400" style={{ fontSize: '18px' }}>
                  waving_hand
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Bem-vindo, <span className="font-semibold text-gray-700 dark:text-gray-200">{username}</span>
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                  {formatTodayLabel(now)}
                </p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
              <span className="material-icons text-blue-600 dark:text-blue-400" style={{ fontSize: '18px' }}>
                insights
              </span>
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900 dark:text-white leading-tight">
                Eventos por Integração
              </h2>
              <div className="mt-1 flex items-stretch gap-2">
                <PeriodToggle period={period} onPeriodChange={onPeriodChange} />
                <ChartTypeToggle chartType={chartType} onChartTypeChange={setChartType} />
                {onRefresh && (
                  <button
                    onClick={onRefresh}
                    disabled={refreshing}
                    title="Atualizar dados"
                    className="flex items-center justify-center px-2.5 rounded-lg text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition disabled:opacity-60 disabled:cursor-not-allowed flex-shrink-0"
                  >
                    <span
                      className={`material-icons ${refreshing ? 'animate-spin' : ''}`}
                      style={{ fontSize: '14px' }}
                    >
                      refresh
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          <div className="flex items-center gap-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2">
            <div className="relative w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700/60 flex items-center justify-center flex-shrink-0">
              <span className="material-icons text-gray-500 dark:text-gray-400" style={{ fontSize: '16px' }}>
                hub
              </span>
              {totalProcessing > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5"
                  title={`${totalProcessing} ${totalProcessing === 1 ? 'evento processando' : 'eventos processando'}`}
                >
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-blue-500" />
                </span>
              )}
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900 dark:text-white leading-tight">{safeStats.length}</p>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">
                {safeStats.length === 1 ? 'Integração' : 'Integrações'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2">
            <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
              <span className="material-icons text-blue-600 dark:text-blue-400" style={{ fontSize: '16px' }}>
                bolt
              </span>
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900 dark:text-white leading-tight">{totalEvents}</p>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">
                {totalEvents === 1 ? 'Evento' : 'Eventos'}
              </p>
            </div>
          </div>

          <span className="w-px self-stretch bg-gray-200 dark:bg-gray-700" />

          <div className="flex items-center divide-x divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 gap-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center flex-shrink-0">
                <span className="material-icons text-emerald-600 dark:text-emerald-400" style={{ fontSize: '16px' }}>
                  check_circle
                </span>
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900 dark:text-white leading-tight">{totalSuccess}</p>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">
                  Sucesso
                </p>
              </div>
            </div>

            <div className="relative group flex items-center gap-2.5 pl-3">
              <div className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0">
                <span className="material-icons text-red-600 dark:text-red-400" style={{ fontSize: '16px' }}>
                  error
                </span>
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900 dark:text-white leading-tight">{totalErrors}</p>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">
                  Falha
                </p>
              </div>
              {totalErrors > 0 && onOpenErrorsPage && (
                // Wrapper starts flush against the cell (top-full = zero gap) and
                // carries the padding itself, so the hoverable area is contiguous
                // from the cell straight down into the button - no dead zone for
                // the cursor to cross (which is what made the old above-card
                // tooltip vanish before the pointer reached it).
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-1.5 opacity-0 invisible pointer-events-none group-hover:opacity-100 group-hover:visible group-hover:pointer-events-auto transition z-20">
                  <button
                    onClick={() => onOpenErrorsPage(safeStats, period)}
                    title="Ver detalhes dos erros"
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-lg text-[11px] font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap hover:text-red-600 dark:hover:text-red-400 hover:border-red-300 dark:hover:border-red-700 transition"
                  >
                    <span className="material-icons leading-none" style={{ fontSize: '12px' }}>north_east</span>
                    Incidências
                  </button>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2.5 pl-3">
              <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
                <span className="material-icons text-blue-600 dark:text-blue-400" style={{ fontSize: '16px' }}>
                  percent
                </span>
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900 dark:text-white leading-tight">
                  {totalFinished > 0 ? `${successRate.toFixed(1)}%` : '—'}
                </p>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">
                  Taxa de sucesso
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showSkeleton ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6">
          {Array.from({ length: Math.max(safeStats.length, 3) }).map((_, idx) => <CardSkeleton key={idx} />)}
        </div>
      ) : isEmpty ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 w-full">
          <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
            <div className="text-center">
              <span className="material-icons text-gray-400 dark:text-gray-600 mb-4" style={{ fontSize: '64px' }}>
                bar_chart
              </span>
              <p className="text-lg font-medium">Sem histórico de eventos</p>
              <p className="text-sm mt-1">Nenhum evento foi processado ainda para esta empresa {PERIOD_EMPTY_LABEL[period] || 'nos últimos 7 dias'}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6">
          {safeStats.map((stat) => (
            <IntegrationEventCard
              key={stat.integration_id}
              stat={stat}
              period={period}
              chartType={chartType}
              integration={integrations?.find((i) => i.id === stat.integration_id)}
              onOpenTracesPage={onOpenTracesPage}
              onOpenErrorsPage={onOpenErrorsPage}
              onTotalsLoaded={handleTotalsLoaded}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default IntegrationsOverview
